import { AlternativeGroup, DiscoveryLayout, mainFromPriceOf, requestedSpecOf } from './listingGroups';

/**
 * The "Specs found" chips on one BOM line and Robbie's one-line suggestion
 * between them. Pure, no React.
 *
 * Chip 1 is the line's own spec ("30C · yours · 1 supplier"); then one chip
 * per alternative spec research found ("40C · 3 suppliers · from ₹3,699").
 * No alternatives → no chips at all.
 */

export interface SpecChip {
  /** '' for the line's own spec; else the alternative's specGroup. */
  specGroup: string;
  /** The spec value(s) alone: "40C". */
  label: string;
  isLine: boolean;
  supplierCount: number;
  fromPrice?: { amount: number; currencySymbol: string };
}

/** "40C", or "4S 40C" when an alternative differs in more than one spec. */
export const alternativeLabelOf = (alt: AlternativeGroup): string => alt.changes.map((c) => c.offered).join(' ');

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const priceText = (price: { amount: number; currencySymbol: string }): string =>
  `${price.currencySymbol}${Math.round(price.amount).toLocaleString()}`;

/** What the chip reads: "30C · yours · 1 supplier" / "40C · 3 suppliers · from ₹3,699". */
export const chipTextOf = (chip: SpecChip): string[] => {
  const count = chip.supplierCount === 0 ? 'no suppliers' : plural(chip.supplierCount, 'supplier');
  if (chip.isLine) return [chip.label, 'yours', count];
  return [chip.label, count, ...(chip.fromPrice ? [`from ${priceText(chip.fromPrice)}`] : [])];
};

export const specChipsOf = (layout: DiscoveryLayout): SpecChip[] => {
  if (layout.alternatives.length === 0) return [];
  const line: SpecChip = {
    specGroup: '',
    label: requestedSpecOf(layout.alternatives),
    isLine: true,
    supplierCount: layout.main.length,
    fromPrice: mainFromPriceOf(layout),
  };
  return [
    line,
    ...layout.alternatives.map((alt) => ({
      specGroup: alt.specGroup,
      label: alternativeLabelOf(alt),
      isLine: false,
      supplierCount: alt.suppliers.length,
      fromPrice: alt.fromPrice,
    })),
  ];
};

export interface SpecSuggestion {
  specGroup: string;
  /** "40C" — the button reads "Switch to 40C". */
  label: string;
  sentence: string;
}

/**
 * Robbie's one suggestion, only when it helps: the line's spec has 0 or 1
 * suppliers, and a safe higher rating ("exceeds") has more suppliers or a
 * lower price. Never for a lower or different spec. Robbie never switches —
 * the buyer does, with the button.
 */
export const specSuggestionOf = (layout: DiscoveryLayout): SpecSuggestion | null => {
  const lineCount = layout.main.length;
  if (lineCount > 1 || layout.alternatives.length === 0) return null;
  const linePrice = mainFromPriceOf(layout)?.amount ?? Infinity;
  const helps = (alt: AlternativeGroup) =>
    alt.suppliers.length > lineCount || (alt.fromPrice != null && alt.fromPrice.amount < linePrice);
  const best = layout.alternatives
    .filter((alt) => alt.kind === 'exceeds' && alt.suppliers.length > 0 && helps(alt))
    .sort(
      (a, b) => b.suppliers.length - a.suppliers.length || (a.fromPrice?.amount ?? Infinity) - (b.fromPrice?.amount ?? Infinity),
    )[0];
  if (!best) return null;

  const ours = requestedSpecOf(layout.alternatives);
  const theirs = alternativeLabelOf(best);
  const opener = lineCount === 0 ? `No supplier sells ${ours}.` : `Only 1 supplier sells ${ours}.`;
  // More suppliers is the stronger reason; a lower price is said only when the counts tie.
  const gain =
    best.suppliers.length > lineCount ? `with ${plural(best.suppliers.length, 'supplier')}` : `from ${priceText(best.fromPrice!)}`;
  return { specGroup: best.specGroup, label: theirs, sentence: `${opener} ${theirs} does the same job, ${gain}.` };
};
